// Builder: convierte el state del formulario en el payload EXACTO requerido.

const FIELD = (ordinal, fieldname, value, pageindex = "1") => ({
  "@pageindex": pageindex,
  "@ordinal": String(ordinal),
  "@fieldname": fieldname,
  "@value": String(value ?? ""),
});

export default function buildEhrNotePayload({
  patient,
  meta,
  location,
  reason,
  vitals,
  hpi,
  meds,
  pmh,
  ros,
  exam,
  phq2,
  assessPlanFu,
}) {
  const fields = [];

  // --- Location + Reason ---
  fields.push(
    FIELD(8852108, "location", location.location),
    FIELD(8852110, "location2", location.location2),
    FIELD(8852112, "location1", location.location1),
    FIELD(8852114, "ReasonforAppointment", reason.reasonForAppointment)
  );

  // --- Vitals ---
  fields.push(
    FIELD(8852119, "Vitals_Weight", vitals.weight),
    FIELD(8852120, "Vitals_Height", vitals.height),
    FIELD(8852121, "Vitals_BMI", vitals.bmi),
    FIELD(8852125, "Vitals_Temperature", vitals.temperature),
    FIELD(8852126, "Vitals_Pulse", vitals.pulse),
    FIELD(8852127, "Vitals_O2SatRA", vitals.o2SatRa),

    FIELD(8852129, "Vitals_BP_SystolicPrev", vitals.bpPrevSys),
    FIELD(8852131, "Vitals_BP_DiastolicPrev", vitals.bpPrevDia),
    FIELD(8852133, "Vitals_BP_Systolic1", vitals.bp1Sys),
    FIELD(8852135, "Vitals_BP_Diastolic1", vitals.bp1Dia),
    FIELD(8852136, "Vitals_BP_Option1", vitals.bpOption1),
    FIELD(8852137, "Vitals_BP_Location1", vitals.bpLocation1)
  );

  // --- HPI + TCM summary ---
  fields.push(
    FIELD(8852140, "HistoryofPresentIllness", hpi.general),
    FIELD(8852142, "HistoryofPresentIllness1", hpi.tcm)
  );

  // --- Medications ---
  fields.push(
    FIELD(
      8852144,
      "Allmedicationsreviewedandreconciled",
      meds.allReviewedAndReconciled ? 1 : 0
    ),
    FIELD(8852146, "CurrentMedications", meds.text)
  );

  // --- PMH ---
  fields.push(
    FIELD(8852149, "PMH", pmh.noChangeReviewed ? 1 : 0),
    FIELD(8852151, "PMH2", pmh.text)
  );

  // --- ROS (sections + items) ---
  const rosMap = [
    [8852154, "Constitutional", ros.Constitutional],
    [8852156, "chills", ros.chills],
    [8852158, "fever", ros.fever],
    [8852160, "lightheadedness", ros.lightheadedness],
    [8852162, "ChangesinAppetite", ros.ChangesinAppetite],
    [8852164, "Fatigue", ros.Fatigue],
    [8852166, "WeightChange", ros.WeightChange],

    [8852167, "Ophthalmologic", ros.Ophthalmologic],
    [8852169, "VisionChanges", ros.VisionChanges],
    [8852171, "Doublevision", ros.Doublevision],
    [8852173, "EyePain", ros.EyePain],

    [8852174, "ENT", ros.ENT],
    [8852176, "Decreasedhearing", ros.Decreasedhearing],
    [8852178, "Ringingintheears", ros.Ringingintheears],
    [8852180, "EarPain", ros.EarPain],
    [8852181, "Nose1", ros.Nose1],
    [8852183, "NoseBleed", ros.NoseBleed],
    [8852185, "NasalCongestion", ros.NasalCongestion],
    [8852187, "sinuspain", ros.sinuspain],
    [8852188, "Throat1", ros.Throat1],
    [8852190, "SoreThroat", ros.SoreThroat],
    [8852192, "Difficultyswallowing", ros.Difficultyswallowing],

    [8852193, "Endocrine", ros.Endocrine],
    [8852195, "Coldintolerance", ros.Coldintolerance],
    [8852197, "Excessivethirst", ros.Excessivethirst],
    [8852199, "frequenturination", ros.frequenturination],
    [8852201, "Heatintolerance", ros.Heatintolerance],
    [8852203, "excessivesweating", ros.excessivesweating],

    [8852204, "Respiratory", ros.Respiratory],
    [8852206, "Shortnessofbreath", ros.Shortnessofbreath],
    [8852208, "Wheezing", ros.Wheezing],
    [8852210, "Hemoptysis", ros.Hemoptysis],
    [8852212, "Cough", ros.Cough],
    [8852214, "sputumproduction", ros.sputumproduction],

    [8852215, "Cardiovascular", ros.Cardiovascular],
    [8852217, "Chestpain", ros.Chestpain],
    [8852219, "ChestpressureorChestdiscomfort", ros.ChestpressureorChestdiscomfort],
    [8852221, "Palpitations", ros.Palpitations],
    [8852223, "Irregularheartbeat", ros.Irregularheartbeat],
    [8852225, "difficultylayingflat", ros.difficultylayingflat],
    [8852227, "dyspneaonexertion", ros.dyspneaonexertion],
    [8852229, "Claudication", ros.Claudication],

    [8852230, "Gastrointestinal", ros.Gastrointestinal],
    [8852232, "Abdominalpain", ros.Abdominalpain],
    [8852234, "Nausea", ros.Nausea],
    [8852236, "vomitting", ros.vomitting],
    [8852238, "changeinbowelhabits", ros.changeinbowelhabits],
    [8852240, "anorexia", ros.anorexia],
    [8852242, "BloodinStool", ros.BloodinStool],
    [8852244, "Diarrhea", ros.Diarrhea],
    [8852246, "Heartburn", ros.Heartburn],

    [8852247, "Genitourinary", ros.Genitourinary],
    [8852249, "bloodintheurine", ros.bloodintheurine],
    [8852251, "difficultyurinating", ros.difficultyurinating],
    [8852253, "paininthelowerback", ros.paininthelowerback],

    [8852254, "Musculoskeletal1", ros.Musculoskeletal1],
    [8852256, "painfuljoints", ros.painfuljoints],
    [8852258, "Swollenjoints", ros.Swollenjoints],
    [8852260, "Cervical", ros.Cervical],
    [8852262, "Thoraxic", ros.Thoraxic],
    [8852264, "Lumbar", ros.Lumbar],

    [8852265, "Neurologic1", ros.Neurologic1],
    [8852267, "Paralysis", ros.Paralysis],
    [8852269, "Seizures", ros.Seizures],
    [8852271, "TinglingNumbness", ros.TinglingNumbness],
    [8852273, "Dizziness1", ros.Dizziness1],
    [8852275, "weakness", ros.weakness],
    [8852277, "Newonsetheadache", ros.Newonsetheadache],
    [8852279, "Difficultyspeaking", ros.Difficultyspeaking],
    [8852281, "Headache", ros.Headache],
    [8852283, "Dizziness", ros.Dizziness],
    [8852285, "lossofstrength", ros.lossofstrength],
    [8852287, "Memoryloss", ros.Memoryloss],
    [8852289, "abnormality", ros.abnormality],

    [8852290, "Psy", ros.Psy],
    [8852292, "Suicidalhomicidalthoughts", ros.Suicidalhomicidalthoughts],
    [8852294, "DifficultySleeping", ros.DifficultySleeping],
  ];
  rosMap.forEach(([ord, name, val]) => fields.push(FIELD(ord, name, val)));

  // --- Examination ---
  const examMap = [
    [8852297, "GeneralAppearance", exam.GeneralAppearance?.on],
    [8852299, "Finding", exam.GeneralAppearance?.text],

    [8852300, "EyesBL", exam.EyesBL?.on],
    [8852302, "Finding1", exam.EyesBL?.text],

    [8852303, "Ears", exam.Ears?.on],
    [8852305, "Finding2", exam.Ears?.text],

    [8852306, "Nose", exam.Nose?.on],
    [8852308, "Finding3", exam.Nose?.text],

    [8852309, "OralCavity", exam.OralCavity?.on],
    [8852311, "Finding4", exam.OralCavity?.text],

    [8852312, "Throat", exam.Throat?.on],
    [8852314, "Finding5", exam.Throat?.text],

    [8852315, "NeckThyroid", exam.NeckThyroid?.on],
    [8852317, "Finding6", exam.NeckThyroid?.text],

    [8852318, "Heart", exam.Heart?.on],
    [8852320, "Finding7", exam.Heart?.text],

    [8852321, "Lungs", exam.Lungs?.on],
    [8852323, "Finding8", exam.Lungs?.text],

    [8852324, "Breast", exam.Breast?.on],
    [8852326, "Finding08", exam.Breast?.text],

    [8852327, "Abdomen", exam.Abdomen?.on],
    [8852329, "Finding9", exam.Abdomen?.text],

    [8852330, "Pelvic", exam.Pelvic?.on],
    [8852332, "Finding09", exam.Pelvic?.text],

    [8852333, "back", exam.back?.on],
    [8852335, "Finding10", exam.back?.text],

    [8852336, "Musculoskeletal", exam.Musculoskeletal?.on],
    [8852338, "Finding11", exam.Musculoskeletal?.text],

    [8852339, "Skin", exam.Skin?.on],
    [8852341, "Finding12", exam.Skin?.text],

    [8852342, "Neurologic", exam.Neurologic?.on],
    [8852344, "Finding13", exam.Neurologic?.text],

    [8852345, "Extremities", exam.Extremities?.on],
    [8852347, "Finding14", exam.Extremities?.text],

    [8852348, "Psychiatric", exam.Psychiatric?.on],
    [8852350, "Finding15", exam.Psychiatric?.text],
  ];
  examMap.forEach(([o, n, v]) => fields.push(FIELD(o, n, v)));

  // --- PHQ-2 ---
  fields.push(
    FIELD(8852353, "PHQ9_1_1", phq2.q1),
    FIELD(8852355, "PHQ9_2_1", phq2.q2),
    FIELD(8852356, "PHQ9scorereviewed", phq2.reviewedPhq9)
  );

  // --- Assessments / Treatment / FollowUp ---
  fields.push(
    FIELD(8852360, "Assessments", assessPlanFu.assessments),
    FIELD(8852362, "Treatment", assessPlanFu.treatment),
    FIELD(8852364, "FollowUp", assessPlanFu.followUp)
  );

  // === Envelope FINAL (sin nombre; SOLO patientId) ===
  return {
    ppmdmsg: {
      "@action": "addehrnote",
      "@class": "api",
      "@msgtime": meta.msgtime,
      "@templatename": meta.templateName,
      "@profileid": patient.profileId,
      "@notedatetime": meta.noteDatetime,
      "@patientid": patient.patientId,
      "@comments": meta.comment,
      "@nocookie": meta.nocookie,
      pagelist: {
        page: [
          {
            "@pageindex": "1",
            "@pagename": "Note",
            fieldlist: { field: fields },
          },
        ],
      },
    },
  };
}
